import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api, { logError, setCurrency } from "../lib/api";
import { PERM_LABEL_BY_KEY } from "../lib/permissions";

const AuthContext = createContext(null);
const PERM_LABELS = PERM_LABEL_BY_KEY;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [estId, setEstId] = useState(() => localStorage.getItem("kavizio_est") || "");
  const permsRef = useRef(null);

  const announceChanges = useCallback((fresh) => {
    const before = permsRef.current;
    permsRef.current = fresh.permissions || {};
    if (!before || fresh.role === "patron") return;
    const removed = Object.keys(PERM_LABELS).filter((k) => before[k] && !fresh.permissions?.[k]);
    const added = Object.keys(PERM_LABELS).filter((k) => !before[k] && fresh.permissions?.[k]);
    if (removed.length) toast.info(`Vos accès ont changé : ${removed.map((k) => PERM_LABELS[k]).join(", ")} n'est plus accessible.`);
    if (added.length) toast.success(`Nouvel accès accordé : ${added.map((k) => PERM_LABELS[k]).join(", ")}.`);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      announceChanges(data);
      setCurrency(data.company?.currency || "GNF");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      permsRef.current = null;
      return null;
    }
  }, [announceChanges]);

  useEffect(() => {
    // Retour d'OAuth : AuthCallback échange le session_id avant tout appel à /auth/me.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  useEffect(() => {
    if (!user) return undefined;
    const t = setInterval(refresh, 20000);
    return () => clearInterval(t);
  }, [user, refresh]);

  const login = useCallback((data) => {
    permsRef.current = data.user.permissions || {};
    setCurrency(data.user.company?.currency || "GNF");
    setUser(data.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      logError("Erreur de déconnexion", e);
    }
    permsRef.current = null;
    setUser(null);
  }, []);

  const pickEstablishment = useCallback((id) => {
    setEstId(id);
    if (id) localStorage.setItem("kavizio_est", id);
    else localStorage.removeItem("kavizio_est");
  }, []);

  const value = useMemo(() => {
    const can = (perm) => !!user && (user.role === "patron" || !!user.permissions?.[perm]);
    const has = (feature) => !!user?.subscription?.features?.includes(feature);
    return { user, setUser, login, logout, can, has, loading, refresh,
             subscription: user?.subscription || null, estId, pickEstablishment };
  }, [user, loading, login, logout, refresh, estId, pickEstablishment]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
